import React from "react";
import { Ticket, Info } from "lucide-react";
import { toast } from "react-toastify";
import TicketTypesSection from "../../events/components/TicketTypesSection";

/**
 * Step Four: Conference Tickets Component
 * Handles overall slots and ticket types configuration for conferences
 */
const StepFourConferenceTickets = ({
  formData,
  onUpdate,
  onBack,
  onNext,
}) => {
  const handleSectionUpdate = (sectionData) => {
    onUpdate({
      ...formData,
      ...sectionData,
    });
  };

  const handleNext = () => {
    // Validate overall slots
    if (!formData.overallSlots || formData.overallSlots <= 0) {
      toast.error("Please specify the overall slots available for the conference");
      return;
    }

    // Validate ticket types
    if (!formData.ticketTypes || formData.ticketTypes.length === 0) {
      toast.error("Please add at least one ticket type");
      return;
    }

    // Validate slots allocation
    const totalAllocatedSlots = formData.ticketTypes.reduce(
      (sum, ticket) => sum + (parseInt(ticket.slotsAllocated) || 0),
      0
    );

    if (totalAllocatedSlots !== parseInt(formData.overallSlots)) {
      toast.error(
        `Total allocated slots (${totalAllocatedSlots}) must equal overall slots (${formData.overallSlots})`
      );
      return;
    }

    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow">
            <Ticket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Conference Tickets
            </h2>
            <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
              Configure ticket types and pricing for your conference
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-3 mt-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800">
              <p className="mb-0.5 font-semibold">💡 Important:</p>
              <ul className="space-y-0.5 text-xs list-disc list-inside">
                <li>Specify the total number of slots available for your conference</li>
                <li>Create different ticket types with their own pricing and slots</li>
                <li>The sum of all ticket slots must equal the overall slots</li>
                <li>Each ticket type can have different prices and benefits</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Types Section */}
      <TicketTypesSection
        overallSlots={formData.overallSlots || ""}
        ticketTypes={formData.ticketTypes || []}
        onOverallSlotsChange={(value) =>
          handleSectionUpdate({ overallSlots: value })
        }
        onTicketTypesChange={(value) =>
          handleSectionUpdate({ ticketTypes: value })
        }
      />

      {/* Navigation Buttons */}
      <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 sm:flex-row">
        <button
          onClick={onBack}
          className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 rounded-lg transition-all flex items-center justify-center space-x-2"
        >
          <span>Back</span>
        </button>
        <button
          onClick={handleNext}
          className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
        >
          <span>Continue to Review</span>
          <Ticket className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StepFourConferenceTickets;

